/* engine.ts — the Atlas canvas, ported VERBATIM from platform/ui/app.js.
 *
 * WHY THIS IS NOT A LIBRARY. ~1,300 lines of Web Mercator, an offline Natural Earth basemap, a
 * six-stop heat ramp, three zoom bands, label collision rejection, corporate-group link arcs, an org
 * overlay and box-select. There is no library equivalent of this specific picture, and the agreed
 * migration plan was to keep it. So the drawing code crossed unchanged and only its INPUTS moved:
 * the old version measured `#maptools` and `#mapstat` out of the DOM inside fit() and labelBox(),
 * which a React view has no business exposing. Those two measurements are now numbers the view sets
 * on MAP (railW, statH, flip) and nothing else about the maths changed.
 *
 * The state object stays MUTABLE and OUTSIDE React on purpose. It holds up to 40,000 points, two
 * Float64Arrays of projected coordinates and an offscreen bitmap; routing that through useState
 * would copy it on every pan.
 */

export interface MapRow {
  id: number; lat: number; lng: number; sqft: number | null; sqft_conf: string | null;
  tier: string | null; committee: number; reach: number; placed: number;
  site: string | null; city: string | null; state: string | null; country: string | null;
  domain: string | null; company: string | null;
}
export type Point = [id: number, lat: number, lng: number, sqft: number, tier: number, comm: number, reach: number, placed: number];
export interface Funnel { total: number; ungeocoded: number; dark_geo: number }
export interface GroupLink { pd: string; cd: string; pn: string; cn: string; plat: number; plng: number; clat: number; clng: number }
export interface OrgMap {
  domain: string; name: string; ungeocoded: number; note: string | null;
  plants: { id: number; lat: number; lng: number; dm: number; site: string | null }[];
  apex: { facility_id: number; full_name: string; title: string; basis: string } | null;
}

interface MapState {
  pts: Point[]; rows: MapRow[]; byId: Record<number, MapRow>;
  byState: { state: string; plants: number }[]; funnel: Funnel | null;
  wx: Float64Array | null; wy: Float64Array | null;
  cx: number; cy: number; scale: number;
  mode: 'committee' | 'reach' | 'sqft' | 'tier';
  density: boolean; labels: boolean; basemap: boolean;
  showLinks: boolean; links: GroupLink[] | null; linkVer: number;
  showOrg: boolean; org: OrgMap | null;
  sel: number | null; radius: number;
  t: number; drag: { x: number; y: number; cx: number; cy: number; shift: boolean } | null;
  box: { x0: number; y0: number; x1: number; y1: number } | null;
  w: number; hgt: number; dpr: number; ver: number; fitted: boolean;
  trunc: number; darkTotal: number | null; showDark: boolean; full: boolean;
  /* Layout measurements the VIEW owns. The old code read them off the DOM; both are the height and
     width of chrome that FLOATS OVER the canvas, and fitting to the full canvas parked the whole
     West Coast underneath the rail. */
  railW: number; statH: number; flip: boolean;
}

export const MAP: MapState = {
  pts: [], rows: [], byId: {}, byState: [], funnel: null, wx: null, wy: null,
  cx: -96, cy: 0, scale: 1, mode: 'committee', density: true, labels: true, basemap: true,
  showLinks: false, links: null, linkVer: 0, showOrg: false, org: null,
  sel: null, radius: 0, t: 0, drag: null, box: null,
  w: 0, hgt: 0, dpr: 1, ver: 0, fitted: false,
  trunc: 0, darkTotal: null, showDark: false, full: false,
  railW: 264, statH: 84, flip: false,
};

const PULSE: { x: number; y: number; r: number; n: number }[] = [];

// Web Mercator, expressed in DEGREE-equivalent units so x (longitude in degrees) and y share one
// scale. Returning raw radians here flattened North America into a 16px band — projections are the
// one place where "looks about right" is not a check.
export const mercY = (lat: number) => (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

// Zoom bands. Level-of-detail is the design, not an optimisation: each band draws a DIFFERENT
// picture rather than the same one scaled up. Continental = the belt as a shape with state names
// ghosted behind it; regional = individual plants with their city; site = the building's own name.
const BAND_INDIVIDUAL = 26;   // below this, plants are binned — the continental "shape" read
const BAND_SITE = 85;         // above this, labels switch from city to the plant's own site name
const LBLFONT = 'Geist, Roboto, "Segoe UI", system-ui, sans-serif';

/* Place names by zoom band. na-cities.json is pre-sorted biggest-first with a band per row, so a
   frame slices the array at the current tier instead of filtering 19,000 records. Bands roughly
   double the label count per step, which keeps the density on screen constant as you zoom. */
const CITY_BANDS: [number, number][] = [[8, 0], [16, 1], [30, 2], [55, 3], [110, 4], [220, 5], [Infinity, 6]];
const cityTier = (s: number) => { for (const [lim, b] of CITY_BANDS) if (s < lim) return b; return 6; };

type City = [name: string, lat: number, lng: number, band: number];
interface Feature { properties: { n: string; code: string; a: string }; geometry: { type: string; coordinates: number[][][] | number[][][][] } }
const GEO: {
  a0: Path2D | null; a1: Path2D | null;
  labels: { n: string; code: string; a: string; x: number; y: number }[];
  cities: City[] | null; loaded: boolean;
} = { a0: null, a1: null, labels: [], cities: null, loaded: false };

/* The basemap is BUNDLED (public/geo), not fetched from a tile server. This deploys behind
   GrayMatter's firewall, so a runtime dependency on the open internet is not available to us —
   which is also why 43.5k vertices are built ONCE as a Path2D in world coordinates and the canvas
   transform does the projection natively every frame. */
export async function loadGeo(): Promise<void> {
  if (GEO.loaded) return;
  GEO.loaded = true;                                   // claim it first: draw() must never double-fetch
  // Places are a separate, non-blocking fetch: 613KB should never hold up the outlines, and if it
  // is missing the map renders exactly as it did before.
  fetch('/geo/na-cities.json').then((r) => r.json())
    .then((c: City[]) => { GEO.cities = c; draw(); }).catch(() => { GEO.cities = null; });
  try {
    const [a0, a1] = await Promise.all([
      fetch('/geo/na-admin0.json').then((r) => r.json()),
      fetch('/geo/na-admin1.json').then((r) => r.json()),
    ]);
    GEO.a0 = pathOf(a0); GEO.a1 = pathOf(a1);
    GEO.labels = (a1.features as Feature[]).map((f) => {
      // Label anchor = centroid of the ring with the most vertices, which is the mainland body for
      // Alaska/BC and their island chains. A bbox centre would sit in the ocean for those.
      let best: number[][] | null = null;
      const polys = (f.geometry.type === 'MultiPolygon'
        ? f.geometry.coordinates as number[][][][]
        : [f.geometry.coordinates as number[][][]]);
      for (const poly of polys) if (!best || poly[0].length > best.length) best = poly[0];
      let x = 0, y = 0;
      for (const c of best!) { x += c[0]; y += mercY(c[1]); }
      return { n: f.properties.n, code: f.properties.code, a: f.properties.a, x: x / best!.length, y: y / best!.length };
    });
    draw();
  } catch { GEO.a0 = GEO.a1 = null; }
}

function pathOf(fc: { features: Feature[] }): Path2D {
  const p = new Path2D();
  for (const f of fc.features) {
    const polys = f.geometry.type === 'MultiPolygon'
      ? f.geometry.coordinates as number[][][][]
      : [f.geometry.coordinates as number[][][]];
    for (const poly of polys) {
      for (const ring of poly) {
        if (ring.length < 2) continue;
        p.moveTo(ring[0][0], mercY(ring[0][1]));
        for (let i = 1; i < ring.length; i++) p.lineTo(ring[i][0], mercY(ring[i][1]));
        p.closePath();
      }
    }
  }
  return p;
}

// World -> screen as a canvas matrix, identical maths to toScreen() so dots and geography can never
// drift apart. y is negated because screen y grows downward while mercator y grows northward.
function worldTransform(g: CanvasRenderingContext2D, dpr: number) {
  g.setTransform(dpr * MAP.scale, 0, 0, -dpr * MAP.scale,
    dpr * (MAP.w / 2 - MAP.scale * MAP.cx), dpr * (MAP.hgt / 2 + MAP.scale * MAP.cy));
}

function drawBasemap(g: CanvasRenderingContext2D, dpr: number) {
  if (!GEO.a0 || !MAP.basemap) return;
  const s = MAP.scale;
  g.save();
  worldTransform(g, dpr);
  // Landmass: one step lighter than the page so the continent reads as a solid body and the ocean
  // recedes. Everything above it is data.
  g.fillStyle = '#101419';
  g.fill(GEO.a0, 'evenodd');
  // State/province hairlines, then country borders a touch stronger. Both fade out at continental
  // zoom so the shape of the landmass leads instead of a mesh of admin lines.
  const fade = Math.min(1, Math.max(0, (s - 3) / 9));
  if (fade > 0.02 && GEO.a1) {
    g.strokeStyle = `rgba(150,166,190,${(0.13 * fade).toFixed(3)})`;
    g.lineWidth = 1 / s; g.stroke(GEO.a1);
  }
  g.strokeStyle = 'rgba(176,192,214,.30)';
  g.lineWidth = 1.3 / s; g.stroke(GEO.a0);
  g.restore();
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
}

// ─────────────────────────────────────────────────────────────────────── view maths

let CANVAS: HTMLCanvasElement | null = null;
export const attach = (cv: HTMLCanvasElement | null) => { CANVAS = cv; };

export function sizeCanvas() {
  const cv = CANVAS; if (!cv) return;
  const r = cv.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = Math.max(300, r.width * dpr); cv.height = Math.max(200, r.height * dpr);
  MAP.dpr = dpr; MAP.w = r.width; MAP.hgt = r.height; draw();
}

/* Fit to where the plants actually ARE, not to their bounding box. Alaska and Hawaii are ~0.2% of
   the points but they used to set the extent on their own, which spent half the frame on empty
   Canada and ocean. Trimming to the 1st–99th percentile puts the industrial belt in the frame. */
export function fit() {
  if (!MAP.pts.length) return;
  const xs = MAP.pts.map((p) => p[2]).sort((a, b) => a - b);
  const ys = MAP.pts.map((p) => mercY(p[1])).sort((a, b) => a - b);
  const qf = (arr: number[], t: number) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor(arr.length * t)))];
  const x0 = qf(xs, 0.01), x1 = qf(xs, 0.99), y0 = qf(ys, 0.01), y1 = qf(ys, 0.99);
  // Fit into the part of the canvas the user can actually SEE. The tool rail and the stat strip sit
  // on top of the map, and fitting to the full canvas parked the whole West Coast under the rail.
  const railW = MAP.railW ? MAP.railW + 24 : 0;
  const padT = 14, padB = MAP.statH + 46;
  const availW = Math.max(120, MAP.w - railW - 18), availH = Math.max(120, MAP.hgt - padT - padB);
  MAP.scale = 0.96 * Math.min(availW / Math.max(1e-6, x1 - x0), availH / Math.max(1e-6, y1 - y0));
  // Then translate so the data lands in the centre of that visible box, not the centre of the canvas.
  const tx = (MAP.flip ? 0 : railW) + availW / 2, ty = padT + availH / 2;
  MAP.cx = (x0 + x1) / 2 - (tx - MAP.w / 2) / MAP.scale;
  MAP.cy = (y0 + y1) / 2 + (ty - MAP.hgt / 2) / MAP.scale;
}

export const toScreen = (lat: number, lng: number): [number, number] => [
  (lng - MAP.cx) * MAP.scale + MAP.w / 2,
  -(mercY(lat) - MAP.cy) * MAP.scale + MAP.hgt / 2,
];

export function zoomAt(px: number, py: number, f: number) {
  const s0 = MAP.scale, s1 = Math.max(0.6, Math.min(400, s0 * f));
  const wx = (px - MAP.w / 2) / s0 + MAP.cx, wy = -(py - MAP.hgt / 2) / s0 + MAP.cy;
  MAP.scale = s1; MAP.cx = wx - (px - MAP.w / 2) / s1; MAP.cy = wy + (py - MAP.hgt / 2) / s1;
  draw();
}

// A single-hue ramp climbing in BRIGHTNESS to GrayMatter's own mark colour. Their primary (#FFAA00)
// and their amber (#FDB614) are neighbours, so a hue-based ramp would collapse: this one moves on
// lightness, a slate that recedes at 0 of 4 and their mark at full brightness at 4 of 4. No green
// anywhere — green reads as "fine", and a plant with a full committee is not fine, it is the thing
// to go and sell.
const RAMP = ['#39404B', '#6B5A28', '#AD8410', '#FDB614', '#FFAA00', '#FF6900'];
const lerp = (a: number[], b: number[], t: number) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
// DERIVED from RAMP, never written twice. It used to be a hand-typed parallel array and it had
// already drifted, which would have let the legend and the map show different colours.
const RAMP_RGB = RAMP.map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));
export function ramp(t: number): string {
  t = Math.max(0, Math.min(1, t));
  const x = t * (RAMP_RGB.length - 1), i = Math.floor(x);
  const c = i >= RAMP_RGB.length - 1 ? RAMP_RGB[RAMP_RGB.length - 1] : lerp(RAMP_RGB[i], RAMP_RGB[i + 1], x - i);
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}
export const RAMP_STOPS = RAMP;

function ptColor(p: number[]): string {
  const [, , , sqft, tier, comm, reach] = p;
  // Tier descends in warmth from the brand mark, so T1 reads loudest without needing a second hue.
  if (MAP.mode === 'tier') return ['#3A414D', '#FFAA00', '#D18B06', '#98670C', '#5A6270'][tier] || '#3A414D';
  if (MAP.mode === 'committee') return RAMP[comm] || RAMP[0];
  if (MAP.mode === 'reach') return ramp(Math.log(1 + reach) / Math.log(260));
  return ramp(Math.log(1 + sqft) / Math.log(2.5e6));
}

/* Everything except the pulse rings and the selection overlay is STATIC between frames — the rAF
   loop exists only to advance the heartbeat. Re-rasterising the basemap, ~1,150 radial gradients and
   14k plants sixty times a second to animate a ring was the whole frame budget. So the static layer
   renders once into an offscreen canvas and blits, and invalidates only when the view, the mode or
   the loaded points actually change. */
const BG: { cv: HTMLCanvasElement | null; key: string; panKey?: string; cx?: number; cy?: number } = { cv: null, key: '' };

const bgKey = () => [MAP.cx, MAP.cy, MAP.scale, MAP.mode, MAP.density, MAP.labels, MAP.basemap,
  // The selection only invalidates the static layer while the group lines are on, because that is
  // the only thing on it that depends on which plant is picked. Otherwise a click would rebuild the
  // density field for nothing.
  MAP.showLinks, MAP.showLinks ? MAP.sel : 0, MAP.linkVer, GEO.cities ? 1 : 0,
  // Same rule for the org layer: it is drawn onto the static bitmap, so the cache has to know both
  // whether it is on and which company it is showing, or toggling it repaints nothing.
  MAP.showOrg, MAP.showOrg && MAP.org ? MAP.org.domain : '',
  MAP.w, MAP.hgt, MAP.dpr, MAP.ver, MAP.railW, MAP.statH, MAP.flip].join('|');

// Everything a pan does NOT change. If any of it moves, the blit shortcut is invalid.
const panKey = (dpr: number) => [MAP.scale, MAP.mode, MAP.density, MAP.labels, MAP.basemap,
  MAP.showLinks, MAP.linkVer, MAP.showOrg, MAP.w, MAP.hgt, dpr, MAP.ver].join('|');

export function draw() {
  const cv = CANVAS; if (!cv || !MAP.w) return;
  const g = cv.getContext('2d'); if (!g) return;
  const dpr = MAP.dpr || 1;
  // A pan is a pure translation of everything static, so while the mouse is down the cached bitmap
  // is blitted at an offset rather than rebuilt. Zoom still rebuilds — scale changes are not a
  // translation and a stretched bitmap would show it.
  if (MAP.drag && !MAP.drag.shift && BG.cv && BG.panKey === panKey(dpr)) {
    const dx = MAP.scale * (BG.cx! - MAP.cx), dy = MAP.scale * (MAP.cy - BG.cy!);
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, cv.width, cv.height);
    g.drawImage(BG.cv, dx * dpr, dy * dpr);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawOverlay(g, true);        // pulse positions belong to the cached frame, so sit it out mid-drag
    return;
  }
  const key = bgKey();
  if (BG.key !== key || !BG.cv || BG.cv.width !== cv.width || BG.cv.height !== cv.height) {
    if (!BG.cv) BG.cv = document.createElement('canvas');
    BG.cv.width = cv.width; BG.cv.height = cv.height;
    PULSE.length = 0;                       // repopulated by the static pass, then reused every frame
    renderStatic(BG.cv.getContext('2d')!, dpr);
    BG.key = key; BG.panKey = panKey(dpr); BG.cx = MAP.cx; BG.cy = MAP.cy;
  }
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, cv.width, cv.height);
  g.drawImage(BG.cv, 0, 0);
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawOverlay(g);
}

function renderStatic(g: CanvasRenderingContext2D, dpr: number) {
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, MAP.w, MAP.hgt);
  // Real geography under the data. The graticule that used to live here was removed: once the
  // landmass and the state borders are drawn, a lat/lng grid is decoration competing with them.
  drawBasemap(g, dpr);
  drawStateLabels(g);
  // Towns sit between the geography and the data. The boxes they claim are handed to the plant
  // labels below so the two layers cannot overlap.
  const cityBoxes = drawCities(g);
  // Group lines go under the plants: they are context for the dots, not a layer that competes.
  drawLinks(g);
  drawOrgStructure(g);
  // The same projection toScreen() applies, hoisted out of the per-point loops as three scalars.
  const sx = MAP.scale, ox = MAP.w / 2 - MAP.scale * MAP.cx, oy = MAP.hgt / 2 + MAP.scale * MAP.cy;

  // ---- density field: the weather-map read. Coarse grid, soft radial blobs, additive, drawn under
  // everything else so concentration is legible before a single dot is examined.
  if (MAP.density && MAP.wx && MAP.wy) {
    const C = 30, field = new Map<number, { x: number; y: number; n: number; w: number }>();
    const P = MAP.pts, PX = MAP.wx, PY = MAP.wy;
    for (let i = 0; i < P.length; i++) {
      const x = PX[i] * sx + ox, y = -PY[i] * sx + oy;
      if (x < -C || y < -C || x > MAP.w + C || y > MAP.hgt + C) continue;
      const p = P[i];
      // +2 keeps the off-screen margin non-negative so two different cells can never hash equal.
      const k = (((x / C) | 0) + 2) * 4096 + (((y / C) | 0) + 2);
      let cur = field.get(k);
      if (!cur) field.set(k, (cur = { x: 0, y: 0, n: 0, w: 0 }));
      cur.x += x; cur.y += y; cur.n++;
      cur.w += MAP.mode === 'reach' ? p[6] : MAP.mode === 'sqft' ? p[3] / 100000 : 1 + p[5];
    }
    // Normalise on the 95th percentile, not the max: Detroit alone would otherwise set the scale and
    // wash the whole continent to one colour. The field is a wash; the dots carry the signal.
    const ws = [...field.values()].map((f) => f.w).sort((a, b) => a - b);
    const p95 = ws[Math.floor(ws.length * 0.95)] || 1;
    g.globalCompositeOperation = 'lighter';
    for (const f of field.values()) {
      const t = Math.min(1, Math.pow(f.w / p95, 0.55));
      if (t < 0.06) continue;
      const r = C * (0.9 + 0.85 * t);
      const gr = g.createRadialGradient(f.x / f.n, f.y / f.n, 0, f.x / f.n, f.y / f.n, r);
      const col = ramp(0.25 + 0.75 * t).replace('rgb(', '').replace(')', '');
      gr.addColorStop(0, `rgba(${col},${(0.13 * t + 0.02).toFixed(3)})`);
      gr.addColorStop(1, `rgba(${col},0)`);
      g.fillStyle = gr;
      g.beginPath(); g.arc(f.x / f.n, f.y / f.n, r, 0, 6.2832); g.fill();
    }
    g.globalCompositeOperation = 'source-over';
  }

  // Zoom-aware: bin while zoomed out, individual plants once they can be told apart. Additive
  // blending was the first attempt and it burned the Midwest to solid white — the colour encoding
  // disappeared exactly where the plants are. Bins keep the encoding honest at every zoom.
  const pts = MAP.pts, wx = MAP.wx, wy = MAP.wy;
  if (!wx || !wy) { g.globalAlpha = 1; return; }
  // One fill per COLOUR instead of one per plant. In committee mode that is 5 draw calls instead of
  // 14,005, and it is the difference between 28fps and a smooth pan. moveTo before each arc keeps
  // the sub-paths from being joined by a stray line.
  const batch = new Map<string, number[]>();
  const add = (col: string, x: number, y: number, r: number) => {
    let a = batch.get(col); if (!a) batch.set(col, (a = [])); a.push(x, y, r);
  };
  const flush = (alpha: number, stroke: boolean) => {
    g.globalAlpha = alpha;
    for (const [col, a] of batch) {
      g.beginPath();
      for (let i = 0; i < a.length; i += 3) { g.moveTo(a[i] + a[i + 2], a[i + 1]); g.arc(a[i], a[i + 1], a[i + 2], 0, 6.2832); }
      g.fillStyle = col; g.fill();
      if (stroke) { g.strokeStyle = 'rgba(0,0,0,.5)'; g.lineWidth = 0.8; g.stroke(); }
    }
    batch.clear();
  };

  if (MAP.scale < BAND_INDIVIDUAL) {
    // Fine bins (7px) with small marks: at continental zoom the job is to render the industrial belt
    // as a SHAPE, and a fine grain of small dots does that where fat ones merged into one mass.
    const B = 7, bins = new Map<number, { x: number; y: number; n: number; sq: number; reach: number; comm: number; tier: number }>();
    for (let i = 0; i < pts.length; i++) {
      const x = wx[i] * sx + ox, y = -wy[i] * sx + oy;
      if (x < -20 || y < -20 || x > MAP.w + 20 || y > MAP.hgt + 20) continue;
      const p = pts[i];
      const k = (((x / B) | 0) + 4) * 4096 + (((y / B) | 0) + 4);
      let b = bins.get(k);
      if (!b) bins.set(k, (b = { x: 0, y: 0, n: 0, sq: 0, reach: 0, comm: 0, tier: 9 }));
      b.x += x; b.y += y; b.n++; b.sq += p[3]; b.reach += p[6];
      // BEST-in-bin, not mean: averaging committee painted the whole continent "1 of 4" because most
      // plants are dark, which buried the only thing the map is for — where a sellable site is.
      if (p[5] > b.comm) b.comm = p[5];
      const t = p[4] || 9; if (t < b.tier) b.tier = t;
    }
    // Two passes so the sellable plants sit forward of the dark ones at a different alpha.
    const hot: (string | number)[] = [];
    for (const b of bins.values()) {
      const x = b.x / b.n, y = b.y / b.n;
      const r = Math.min(4, 1 + Math.sqrt(b.n) * 0.5 + Math.sqrt(b.sq / 1e6) * 0.25);
      const col = ptColor([0, 0, 0, b.sq / b.n, b.tier === 9 ? 0 : b.tier, b.comm, b.reach]);
      if (b.comm >= 3) hot.push(col, x, y, r); else add(col, x, y, r);
      // a cluster carrying a full committee gets a slow pulse: that is where a rep should look
      if (b.comm === 4 && b.n >= 2) PULSE.push({ x, y, r, n: b.n });
    }
    flush(0.62, false);
    for (let i = 0; i < hot.length; i += 4) add(hot[i] as string, hot[i + 1] as number, hot[i + 2] as number, hot[i + 3] as number);
    flush(0.95, false);
  } else {
    for (let i = pts.length - 1; i >= 0; i--) {
      const x = wx[i] * sx + ox, y = -wy[i] * sx + oy;
      if (x < -20 || y < -20 || x > MAP.w + 20 || y > MAP.hgt + 20) continue;
      const p = pts[i];
      const r = Math.max(2, Math.min(7, 2 + Math.sqrt(p[3] || 1) / 320));
      add(ptColor(p), x, y, r);
      if (p[5] === 4) PULSE.push({ x, y, r: Math.max(r, 3.5), n: 1 });
    }
    flush(0.92, true);
  }
  g.globalAlpha = 1;
  drawLabels(g, cityBoxes);
  // Only the 70 loudest pulses ever render, so rank once here rather than every animation frame.
  if (PULSE.length > 70) { PULSE.sort((a, b) => b.n - a.n); PULSE.length = 70; }
}

/* The rail and the stat strip float over the canvas, so text placed under them is text nobody can
   read. Every label pass clips to the box that is actually visible. The rail flips to the right-hand
   gutter, and this box used to assume it was always on the left — so in flipped mode it reserved
   space on the empty side and let labels run under the rail on the full one. */
function labelBox() {
  const w = MAP.railW ? MAP.railW + 20 : 8;
  return {
    l: MAP.flip ? 8 : w, t: 14,
    r: MAP.w - (MAP.flip ? w : 6),
    // +72 rather than +44: the colour key and the zoom caption are drawn in the band just above the
    // stat row, and at the old reserve a place name could land on top of them.
    b: MAP.hgt - (MAP.statH + 72),
  };
}

function drawStateLabels(g: CanvasRenderingContext2D) {
  if (!MAP.labels || !GEO.labels.length) return;
  const s = MAP.scale;
  const B = labelBox();
  const sz = Math.max(9, Math.min(15, 4 + s * 0.46));
  g.save();
  g.font = `600 ${sz.toFixed(1)}px ${LBLFONT}`;
  const gg = g as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in gg) gg.letterSpacing = '1.8px';
  g.fillStyle = `rgba(196,212,232,${(s < BAND_INDIVIDUAL ? 0.15 : 0.08).toFixed(3)})`;
  g.textAlign = 'center';
  const taken: { x: number; y: number; w: number; h: number }[] = [];
  for (const L of GEO.labels) {
    const x = (L.x - MAP.cx) * s + MAP.w / 2, y = -(L.y - MAP.cy) * s + MAP.hgt / 2;
    const txt = (s < 9 && L.code ? L.code : L.n).toUpperCase();
    const w = g.measureText(txt).width;
    const bx = x - w / 2;
    if (bx < B.l || y - sz < B.t || bx + w > B.r || y > B.b) continue;
    let clash = false;
    for (const t of taken) if (bx < t.x + t.w + 6 && bx + w + 6 > t.x && y - sz < t.y + t.h + 4 && y + 4 > t.y) { clash = true; break; }
    if (clash) continue;
    taken.push({ x: bx, y: y - sz, w, h: sz });
    g.fillText(txt, x, y);
  }
  g.restore();
  if ('letterSpacing' in gg) gg.letterSpacing = '0px';
}

/* Towns. Drawn under the plant layer and deliberately quiet: this is the geography a rep needs to
   orient by, not data. Returns the boxes it used so the plant labels can avoid them — two labels on
   top of each other is worse than one label missing. */
function drawCities(g: CanvasRenderingContext2D) {
  const used: { x: number; y: number; w: number; h: number }[] = [];
  if (!MAP.labels || !GEO.cities) return used;
  const s = MAP.scale;
  const tier = cityTier(s);
  const B = labelBox();
  g.save();
  g.textAlign = 'left'; g.textBaseline = 'alphabetic';
  let drawn = 0;
  for (const [name, lat, lng, band] of GEO.cities) {
    if (band > tier) break;                     // pre-sorted: nothing past here can qualify either
    if (drawn > 220) break;                     // a hard ceiling on the frame cost, whatever the zoom
    const [x, y] = toScreen(lat, lng);
    if (x < B.l || y < B.t || x > B.r || y > B.b) continue;
    const rank = tier - band;                   // 0 = the biggest thing on screen
    const sz = Math.max(9, 12.5 - rank * 0.9);
    const dot = Math.max(1.1, 2.6 - rank * 0.35);
    g.font = `${rank < 1 ? 600 : 400} ${sz.toFixed(1)}px ${LBLFONT}`;
    const w = g.measureText(name).width;
    const bx = x + dot + 4, by = y - sz + 2;
    let clash = false;
    for (const t of used) if (bx < t.x + t.w + 5 && bx + w + 5 > t.x && by < t.y + t.h + 3 && by + sz + 3 > t.y) { clash = true; break; }
    if (clash) continue;
    used.push({ x: bx, y: by, w, h: sz });
    const a = Math.max(0.24, 0.62 - rank * 0.1);
    g.fillStyle = `rgba(150,164,184,${(a * 0.75).toFixed(3)})`;
    g.beginPath(); g.arc(x, y, dot, 0, 6.2832); g.fill();
    g.fillStyle = 'rgba(0,0,0,.6)'; g.fillText(name, bx + 0.6, y + 3.6);
    g.fillStyle = `rgba(178,192,212,${a.toFixed(3)})`;
    g.fillText(name, bx, y + 3);
    drawn++;
  }
  g.restore();
  return used;
}

/* Corporate-group lines.
   The first version drew all 1,247 at once and the result was a ball of wool over the industrial
   belt. What is useful is the group you are LOOKING at:
     · a plant selected  -> only that company's group, whatever the zoom. This is the real use.
     · nothing selected  -> only past regional zoom, and capped, so it reads as structure not noise.
   Curves rather than straight segments, because two edges sharing an endpoint separate visibly.
   Parent end is a filled node, child end an open one, so direction needs no legend. */
const LINK_ZOOM = 22;
const LINK_GROUPS = 12;       // distinct parents drawn when nothing is selected

export function linksToDraw(): GroupLink[] {
  if (!MAP.showLinks || !MAP.links || !MAP.links.length) return [];
  const selDom = MAP.sel && MAP.byId[MAP.sel] ? MAP.byId[MAP.sel].domain : null;
  if (selDom) return MAP.links.filter((l) => l.pd === selDom || l.cd === selDom);
  if (MAP.scale < LINK_ZOOM) return [];
  const on = (x: number, y: number) => x > -40 && y > -40 && x < MAP.w + 40 && y < MAP.hgt + 40;
  const out: GroupLink[] = [], groups = new Set<string>();
  for (const l of MAP.links) {
    const [x1, y1] = toScreen(l.plat, l.plng);
    const [x2, y2] = toScreen(l.clat, l.clng);
    if (!on(x1, y1) && !on(x2, y2)) continue;
    if (!groups.has(l.pd)) { if (groups.size >= LINK_GROUPS) continue; groups.add(l.pd); }
    out.push(l);
    if (out.length >= 90) break;
  }
  return out;
}
export const LINK_ZOOM_LEVEL = LINK_ZOOM;

function drawLinks(g: CanvasRenderingContext2D) {
  const list = linksToDraw();
  if (!list.length) return;
  const s = MAP.scale;
  g.save();
  g.lineWidth = Math.min(1.8, Math.max(0.7, s / 55));
  for (const l of list) {
    const [x1, y1] = toScreen(l.plat, l.plng);
    const [x2, y2] = toScreen(l.clat, l.clng);
    // Both ends off screen on the same side: nothing to draw.
    if ((x1 < 0 && x2 < 0) || (y1 < 0 && y2 < 0) || (x1 > MAP.w && x2 > MAP.w) || (y1 > MAP.hgt && y2 > MAP.hgt)) continue;
    const dx = x2 - x1, dy = y2 - y1;
    const d = Math.hypot(dx, dy);
    if (d < 3) continue;
    // Bow perpendicular to the chord, proportional to its length and capped so long hauls stay sane.
    const bow = Math.min(60, d * 0.16);
    const mx = (x1 + x2) / 2 - (dy / d) * bow, my = (y1 + y2) / 2 + (dx / d) * bow;
    const grad = g.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, 'rgba(167,139,250,.75)');
    grad.addColorStop(1, 'rgba(167,139,250,.2)');
    g.strokeStyle = grad;
    g.beginPath(); g.moveTo(x1, y1); g.quadraticCurveTo(mx, my, x2, y2); g.stroke();
    g.fillStyle = 'rgba(167,139,250,.95)';
    g.beginPath(); g.arc(x1, y1, 3, 0, 6.2832); g.fill();
    g.strokeStyle = 'rgba(167,139,250,.8)';
    const lw = g.lineWidth; g.lineWidth = 1.2;
    g.beginPath(); g.arc(x2, y2, 2.4, 0, 6.2832); g.stroke();
    g.lineWidth = lw;
  }
  g.restore();
}

/* ORG STRUCTURE. Corporate groups say which COMPANIES belong together. This says, inside one
   company, where the authority sits: a ring on the building holding the most senior person we can
   reach, and a spoke out to every other building of that company.
   Spokes are solid where the far plant has a decision maker on file and dashed where it does not,
   so the shape of the account reads without a legend.
   What this is NOT: a reporting line. There are none in the source. Seniority is read off job
   titles through the same adjusted level the org chart uses, and the panel says so in those words. */
function drawOrgStructure(g: CanvasRenderingContext2D) {
  const O = MAP.org;
  if (!MAP.showOrg || !O || !O.plants || !O.plants.length) return;
  const apexId = O.apex && O.apex.facility_id;
  const hub = apexId ? O.plants.find((p) => p.id === apexId) : null;
  const s = MAP.scale;
  g.save();
  if (hub) {
    const [hx, hy] = toScreen(hub.lat, hub.lng);
    g.lineWidth = Math.min(1.6, Math.max(0.6, s / 70));
    for (const p of O.plants) {
      if (p.id === hub.id) continue;
      const [x, y] = toScreen(p.lat, p.lng);
      if ((x < -60 && hx < -60) || (x > MAP.w + 60 && hx > MAP.w + 60)) continue;
      const dx = x - hx, dy = y - hy, d = Math.hypot(dx, dy);
      if (d < 2) continue;
      const bow = Math.min(48, d * 0.12);
      const mx = (hx + x) / 2 - (dy / d) * bow, my = (hy + y) / 2 + (dx / d) * bow;
      // Solid = this building has a decision maker of its own. Dashed = it does not.
      g.setLineDash(p.dm > 0 ? [] : [3, 4]);
      g.strokeStyle = p.dm > 0 ? 'rgba(255,170,0,.72)' : 'rgba(160,172,190,.45)';
      g.beginPath(); g.moveTo(hx, hy); g.quadraticCurveTo(mx, my, x, y); g.stroke();
    }
    g.setLineDash([]);
    // The hub itself: a double ring so it survives being drawn over by its own plant dot.
    g.strokeStyle = 'rgba(255,170,0,.95)'; g.lineWidth = 2;
    g.beginPath(); g.arc(hx, hy, 9, 0, 6.2832); g.stroke();
    g.strokeStyle = 'rgba(255,170,0,.35)'; g.lineWidth = 1;
    g.beginPath(); g.arc(hx, hy, 13, 0, 6.2832); g.stroke();
  }
  g.restore();
}

/* Labels are the clearest expression of the zoom bands, so they get their own pass with real
   collision rejection — overlapping text is what makes a map look cheap, and at 33k points a naive
   "draw them all" produces a smear exactly where the plants are densest. Place names go UNDER the
   plants: they orient you, they do not compete with the data. */
function drawLabels(g: CanvasRenderingContext2D, cityBoxes: { x: number; y: number; w: number; h: number }[]) {
  if (!MAP.labels) return;
  const s = MAP.scale;
  if (s < BAND_INDIVIDUAL) return;   // continental band names states only, drawn under
  const taken = (cityBoxes || []).slice();
  const B = labelBox();
  const fits = (x: number, y: number, w: number, hh: number) => {
    if (x < B.l || y < B.t || x + w > B.r || y + hh > B.b) return false;
    for (const t of taken) if (x < t.x + t.w + 5 && x + w + 5 > t.x && y < t.y + t.h + 3 && y + hh + 3 > t.y) return false;
    taken.push({ x, y, w, h: hh }); return true;
  };
  // Biggest first (MAP.rows is sqft-descending) so the labels that survive collision are the ones
  // worth reading. The cap is deliberately low — a label on every plant is a smear.
  const site = s >= BAND_SITE;
  g.font = `600 ${site ? 11 : 10}px ${LBLFONT}`;
  let drawn = 0; const cap = site ? 55 : 26;
  const seen = new Set<string>();
  for (const r of MAP.rows) {
    if (drawn >= cap) break;
    if (r.lat == null) continue;
    const txt = site ? (r.site || r.company || '') : `${r.city || ''}${r.state ? ', ' + r.state : ''}`;
    if (!txt) continue;
    if (!site) { if (seen.has(txt)) continue; seen.add(txt); }   // one label per city, not per plant
    const [x, y] = toScreen(r.lat, r.lng);
    const w = g.measureText(txt).width;
    if (!fits(x + 8, y - 12, w, 11)) continue;
    // A hair of shadow keeps the text readable where it crosses a bright density bloom.
    g.fillStyle = 'rgba(0,0,0,.75)'; g.fillText(txt, x + 9, y - 3.4);
    g.fillStyle = r.committee === 4 ? 'rgba(255,170,60,.95)' : 'rgba(226,234,246,.72)';
    g.fillText(txt, x + 8.4, y - 4);
    drawn++;
  }
}

// Per-frame layer: the heartbeat, the selection, the drag box and the legend. Everything here is
// cheap by construction — anything expensive in this function and the map stops being smooth.
function drawOverlay(g: CanvasRenderingContext2D, skipPulse?: boolean) {
  // ---- pulse rings. Capped and eased so it reads as a heartbeat, not a disco.
  if (PULSE.length && !skipPulse) {
    const ph = (MAP.t % 2200) / 2200;
    const e = 1 - Math.pow(1 - ph, 2);
    g.lineWidth = 1.4;
    g.strokeStyle = `rgba(255,170,0,${(0.55 * (1 - e)).toFixed(3)})`;
    g.beginPath();
    for (const q of PULSE) { const rr = q.r + 2 + e * 13; g.moveTo(q.x + rr, q.y); g.arc(q.x, q.y, rr, 0, 6.2832); }
    g.stroke();
  }
  // selection + radius
  if (MAP.sel && MAP.byId[MAP.sel]) {
    const s = MAP.byId[MAP.sel]; const [x, y] = toScreen(s.lat, s.lng);
    g.strokeStyle = '#fff'; g.lineWidth = 1.5; g.beginPath(); g.arc(x, y, 8, 0, 6.2832); g.stroke();
    g.strokeStyle = '#FFAA00'; g.beginPath(); g.arc(x, y, 12, 0, 6.2832); g.stroke();
    if (MAP.radius) {
      const milesPerDeg = 69 * Math.cos((s.lat * Math.PI) / 180);
      const rpx = (MAP.radius / milesPerDeg) * MAP.scale;
      g.strokeStyle = 'rgba(255,170,0,.55)'; g.setLineDash([4, 4]);
      g.beginPath(); g.arc(x, y, rpx, 0, 6.2832); g.stroke(); g.setLineDash([]);
      g.fillStyle = 'rgba(255,170,0,.05)'; g.fill();
    }
  }
  if (MAP.box) {
    const b = MAP.box; g.strokeStyle = '#3DA5FF'; g.setLineDash([3, 3]);
    g.strokeRect(Math.min(b.x0, b.x1), Math.min(b.y0, b.y1), Math.abs(b.x1 - b.x0), Math.abs(b.y1 - b.y0));
    g.setLineDash([]);
  }
  // legend. The "0 of 4" swatch only appears when dark plants are actually being drawn — a legend
  // entry for something not on screen is the same class of lie as a stat that reads zero because it
  // is hidden.
  const LEG: [string, string][] = ({
    committee: [...(MAP.showDark ? [['0 of 4 — dark building', RAMP[0]] as [string, string]] : []),
      ['1 of 4', RAMP[1]], ['2', RAMP[2]], ['3', RAMP[3]], ['4 of 4 — sellable, pulsing', RAMP[4]]],
    tier: [['T1', '#FFAA00'], ['T2', '#D18B06'], ['T3', '#98670C'], ['T4', '#3A414D']],
    reach: [['nobody reachable', ramp(0)], ['a few', ramp(0.5)], ['many', ramp(1)]],
    sqft: [['small', ramp(0)], ['mid', ramp(0.5)], ['1M+ sq ft', ramp(1)]],
  } as Record<string, [string, string][]>)[MAP.mode] || [];
  g.font = `10px ${LBLFONT}`;
  const ly = MAP.hgt - (MAP.statH + 22);
  /* Start clear of the filter rail. Both of these lines were drawn at x=14 while the rail floats
     over the left gutter, so the first 234px of the colour key was painted underneath it.
     Unconditional rather than "only when the rail is tall enough to reach this row": the rail's
     height changes with which sections are open. */
  const lx0 = labelBox().l;
  let lx = lx0;
  for (const [lbl, c] of LEG) {
    g.fillStyle = c; g.beginPath(); g.arc(lx + 4, ly, 4.2, 0, 6.2832); g.fill();
    g.fillStyle = 'rgba(255,255,255,.55)'; g.fillText(lbl, lx + 12, ly + 3.5);
    lx += 22 + g.measureText(lbl).width;
  }
  g.fillStyle = 'rgba(255,255,255,.3)'; g.font = '10px ui-monospace, Consolas, monospace';
  g.fillText(
    `${MAP.pts.length.toLocaleString('en-US')} plants · ${MAP.scale < BAND_INDIVIDUAL
      ? 'binned — colour is the best plant in the bin, size is how many'
      : MAP.scale < BAND_SITE ? 'individual plants' : 'individual plants, named'} · zoom ${MAP.scale.toFixed(1)}`,
    lx0, ly - 17,
  );
}

// One rAF loop, only while the Atlas is on screen, only to advance the pulse phase. A map that
// animates when nobody is looking at it is just a fan spinning up.
let RAF = 0;
export function startPulse() {
  if (RAF) return;
  const tick = (ts: number) => { MAP.t = ts; draw(); RAF = requestAnimationFrame(tick); };
  RAF = requestAnimationFrame(tick);
}
export function stopPulse() { if (RAF) { cancelAnimationFrame(RAF); RAF = 0; } }

/* Leaving the tab releases the Atlas's working set — the largest retained allocation in the app (up
   to 40k points, the row objects, the projected Float64Arrays and the offscreen static layer). The
   view reloads all of it on every entry anyway, and `fitted` goes false so the next visit re-fits to
   freshly loaded points rather than framing arrays that no longer exist. */
export function freeAtlas() {
  MAP.pts = []; MAP.rows = []; MAP.byId = {}; MAP.byState = []; MAP.funnel = null;
  MAP.wx = null; MAP.wy = null; MAP.links = null; MAP.org = null; MAP.trunc = 0;
  MAP.fitted = false;
  BG.cv = null; BG.key = '';
}

/* Ingest one /api/map response. Projects ONCE per load, not once per point per frame: mercY is a log
   and a tan, and running it over 14k points inside the rAF loop was most of the frame budget. */
export function setPoints(r: {
  points: Point[]; rows: MapRow[]; byState: { state: string; plants: number }[];
  funnel: Funnel; truncated?: boolean; pointTotal?: number;
}) {
  MAP.pts = r.points; MAP.rows = r.rows; MAP.byState = r.byState;
  MAP.funnel = r.funnel; MAP.darkTotal = r.funnel ? r.funnel.dark_geo : MAP.darkTotal;
  // The points query carries a 40k cap. When the server reports it tripped, the funnel says so —
  // otherwise the overflow would silently read as "filtered out".
  MAP.trunc = r.truncated ? (r.pointTotal || 0) : 0;
  MAP.byId = Object.fromEntries(r.rows.map((x) => [x.id, x]));
  const n = MAP.pts.length;
  MAP.wx = new Float64Array(n); MAP.wy = new Float64Array(n);
  for (let i = 0; i < n; i++) { MAP.wx[i] = MAP.pts[i][2]; MAP.wy[i] = mercY(MAP.pts[i][1]); }
  MAP.ver++;      // invalidates the cached static layer — the points changed
}

/* The precomputed projection renderStatic already uses, with its three hoisted scalars. This runs on
   every mousemove; calling toScreen() per point re-did the tan+log and allocated a fresh pair for up
   to 40k points per pointer event. */
export function nearest(px: number, py: number): Point | null {
  const wx = MAP.wx, wy = MAP.wy, pts = MAP.pts;
  if (!wx || !wy) return null;
  const sx = MAP.scale, ox = MAP.w / 2 - MAP.scale * MAP.cx, oy = MAP.hgt / 2 + MAP.scale * MAP.cy;
  let best: Point | null = null, bd = 14 * 14;
  for (let i = 0; i < pts.length; i++) {
    const dx = wx[i] * sx + ox - px, dy = -wy[i] * sx + oy - py;
    const d = dx * dx + dy * dy;
    if (d < bd) { bd = d; best = pts[i]; }
  }
  return best;
}

export function inBox(b: { x0: number; y0: number; x1: number; y1: number }): MapRow[] {
  const x0 = Math.min(b.x0, b.x1), x1 = Math.max(b.x0, b.x1);
  const y0 = Math.min(b.y0, b.y1), y1 = Math.max(b.y0, b.y1);
  return MAP.rows.filter((r) => {
    const [x, y] = toScreen(r.lat, r.lng);
    return x >= x0 && x <= x1 && y >= y0 && y <= y1;
  });
}
